/**
 * File: Table.js
 * Origin: isotect
 * Description: Table component renders a html table.
 *
 * Version: v1.1.0
 * Created: 2026-04-27
 * Last Updated: 2026-07-25
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { useMemo, useState } from "react";

// Local project files
import { sortTableRows, transposeTable } from "../../utils/tableUtilities";
import Checkbox from "../forms/Checkbox";

// Icons
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa6";

// Constants
const sortConfig = {
    default: {
        next: "asc",
        icon: <FaSort />,
    },
    asc: {
        next: "desc",
        icon: <FaSortUp />,
    },
    desc: {
        next: "default",
        icon: <FaSortDown />,
    },
};

const cellConfig = {
    data: {
        array: {
            render: (values) => (
                <div>
                    {values.map((value) => (
                        <p>{value}</p>
                    ))}
                </div>
            ),
            className: "table-data--array",
        },
        boolean: {
            render: (value) => (value ? "Yes" : "No"),
            className: "table-data--boolean",
        },
        checkbox: {
            render: (_, isSelected, onClick) => (
                <Checkbox checked={isSelected} onChange={onClick} />
            ),
            className: "table-data--checkbox",
        },
        currency: {
            render: (value) =>
                value == null
                    ? "—"
                    : new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "USD",
                      }).format(value),
            className: "table-data--currency",
        },
        date: {
            render: (value) =>
                value ? new Date(value).toLocaleDateString() : "—",
            className: "table-data--date",
        },
        datetime: {
            render: (value) =>
                value ? (
                    <>
                        <div>{new Date(value).toLocaleDateString()}</div>
                        <div>{new Date(value).toLocaleTimeString()}</div>
                    </>
                ) : (
                    "—"
                ),
            className: "table-data--datetime",
        },
        id: {
            render: (id) => (id != null ? `...${String(id).slice(-6)}` : ""),
            className: "table-data--id",
        },
        ipAddress: {
            render: (value) => value ?? "—",
            className: "table-data--ip-address",
        },
        number: {
            render: (value) => value?.toLocaleString() ?? "—",
            className: "table-data--number",
        },
        percent: {
            render: (value) =>
                value == null ? "—" : `${(value * 100).toFixed(1)}%`,
            className: "table-data--percent",
        },
        text: {
            render: (value) => value,
            className: "table-data--text",
        },
        textLeft: {
            render: (value) => value,
            className: "table-data--text-left",
        },
        textCenter: {
            render: (value) => value,
            className: "table-data--text-center",
        },
        textRight: {
            render: (value) => value,
            className: "table-data--text-right",
        },
    },
    header: {
        checkbox: {
            render: (_, isSelected, onClick) => (
                <Checkbox checked={isSelected} onChange={onClick} />
            ),
            className: "table-header--checkbox",
        },
        text: {
            render: (value) => value,
            className: "table-header--text",
        },
    },
};

/**
 * Renders a paginated HTML table with optional sorting, row selection,
 * transposed layout, and configurable table sections.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {string} props.id - Unique table identifier
 * @param {Object<string, Object>} props.headers - Column configuration keyed by column name
 * @param {Array<Object>} props.rows - Array of row objects used as table data
 *
 * @param {Object} [props.appearance={}] - Table appearance options
 * @param {boolean} [props.appearance.transposed=false] - Displays the table in a transposed layout
 *
 * @param {Object} [props.components={}] - Table content and rendered sections
 * @param {string|false} [props.components.caption=false] - Whether to render the table caption
 * @param {boolean} [props.components.header=true] - Whether to render the table header
 * @param {boolean} [props.components.footer=false] - Whether to render the table footer
 *
 * @param {Object} [props.features={}] - Optional table features
 * @param {boolean} [props.features.selectable=false] - Enables row selection with checkboxes
 *
 * @param {Object} [props.state={}] - Controlled table state
 * @param {boolean} [props.state.allRowsSelected=false] - Whether all rows are selected
 * @param {number} [props.state.currentPage=1] - Current page for pagination
 * @param {number} [props.config.rowsPerPage=10] - Number of rows displayed per page
 * @param {Array<number>} [props.state.selectedRows=[]] - Indices of the selected rows
 *
 * @param {Object} [props.callbacks={}] - Callback functions for controlled state
 * @param {(allRowsSelected: boolean) => void} [props.callbacks.setAllRowsSelected] - Updates the "select all" state
 * @param {(selectedRows: number[]) => void} [props.callbacks.setSelectedRows] - Updates the selected rows
 *
 * @returns {JSX.Element} The rendered table component
 */
function Table({
    id,
    headers,
    rows,
    appearance = {},
    components = {},
    features = {},
    state = {},
    callbacks = {},
}) {
    const { transposed = false } = appearance;
    const { caption = false, header = true, footer = false } = components;
    const { selectable = false } = features;

    const {
        allRowsSelected = false,
        currentPage = 1,
        rowsPerPage = 10,
        selectedRows = [],
    } = state;

    const { setAllRowsSelected, setSelectedRows } = callbacks;

    const [columnSort, setColumnSort] = useState({
        index: null,
        order: "default",
    });

    const columns = Object.values(headers);

    const keys = columns.map((column) => column.key);
    const labels = columns.map((column) => column.label);
    const units = columns.map((column) => column.unit ?? "[-]");
    const types = columns.map((column) => column.type ?? "text");

    const sortedRows = useMemo(() => {
        if (columnSort.index === null || columnSort.order === "default") {
            return rows;
        }

        return sortTableRows(
            rows,
            keys[columnSort.index],
            columnSort.order === "asc",
        );
    }, [rows, columnSort, keys]);

    if (!Array.isArray(rows)) {
        return <p>Error: Table data is not an array</p>;
    }

    const isValidTable = rows.every(
        (row) => Object.keys(row).length === columns.length,
    );

    if (!isValidTable) {
        return <p>Error: Number of columns in each row do not match</p>;
    }

    const startIndex = (currentPage - 1) * rowsPerPage;

    const paginatedRows = sortedRows.slice(
        startIndex,
        startIndex + rowsPerPage,
    );

    const tableRows = paginatedRows.map((row) => Object.values(row));

    const displayRows = transposed
        ? transposeTable(labels, tableRows)
        : tableRows;

    const handleColumnSort = (index, order) => {
        setColumnSort({ index, order });
    };

    const handleRowSelection = (rowIndex) => {
        setSelectedRows((prev) => {
            const exists = prev.includes(rowIndex);

            const updated = exists
                ? prev.filter((index) => index !== rowIndex)
                : [...prev, rowIndex];

            if (allRowsSelected && exists && setAllRowsSelected) {
                setAllRowsSelected(false);
            }

            return updated;
        });
    };

    const handleAllRowsSelection = () => {
        if (!setSelectedRows || !setAllRowsSelected) {
            return;
        }

        setAllRowsSelected((prev) => {
            if (prev) {
                setSelectedRows([]);
            } else {
                setSelectedRows(rows.map((_, index) => index));
            }

            return !prev;
        });
    };

    return (
        <>
            <table id={id} className="table">
                {caption && <caption>{caption}</caption>}
                {header && !transposed && (
                    <thead>
                        <TableRow
                            values={labels}
                            types={types}
                            appearance={{ header: true }}
                            features={{ selectable }}
                            state={{
                                columnSort,
                                selected: allRowsSelected,
                            }}
                            callbacks={{
                                setColumnSort: handleColumnSort,
                                setSelected: handleAllRowsSelection,
                            }}
                        />
                    </thead>
                )}
                <tbody>
                    {displayRows.map((row, index) => {
                        const actualIndex = startIndex + index;
                        return (
                            <TableRow
                                key={index}
                                values={row}
                                types={transposed ? [types[index]] : types}
                                appearance={{ transposed }}
                                features={{
                                    selectable: !transposed && selectable,
                                }}
                                state={{
                                    selected:
                                        selectedRows.includes(actualIndex),
                                }}
                                callbacks={{
                                    setSelected: () =>
                                        handleRowSelection(actualIndex),
                                }}
                            />
                        );
                    })}
                </tbody>
                {footer && (
                    <tfoot>
                        <tr>
                            <td colSpan={labels.length + 1}>
                                {displayRows.length}
                            </td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </>
    );
}

/**
 * Renders a table row from an array of values.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {Array<*>} props.values - Values displayed in the row's cells
 * @param {Array<string>} [props.types=[]] - Data type for each cell value
 *
 * @param {Object} [props.appearance={}] - Row appearance options
 * @param {boolean} [props.appearance.header=false] - Renders the row as a header row
 * @param {boolean} [props.appearance.transposed=false] - Renders the row in a transposed layout
 *
 * @param {Object} [props.features={}] - Optional row features
 * @param {boolean} [props.features.selectable=false] - Prepends a selection checkbox to the row
 *
 * @param {Object} [props.state={}] - Controlled row state
 * @param {{index: number|null, order: string}} [props.state.columnSort] - Current column sort state
 * @param {boolean} [props.state.selected=false] - Whether the row is selected
 *
 * @param {Object} [props.callbacks={}] - Callback functions for row interactions
 * @param {(index: number, order: string) => void} [props.callbacks.setColumnSort] - Updates the sort order for a column
 * @param {() => void} [props.callbacks.setSelected] - Toggles the row selection state
 *
 * @returns {JSX.Element} The rendered table row
 */
function TableRow({
    values,
    types = [],
    appearance = {},
    features = {},
    state = {},
    callbacks = {},
}) {
    const { header = false, transposed = false } = appearance;
    const { selectable = false } = features;
    const { columnSort, selected = false } = state;
    const { setColumnSort, setSelected } = callbacks;

    const Checkbox = header ? TableHeader : TableData;

    return (
        <tr
            className={`${header ? "table-row--header" : "table-row--data"} ${
                selected && !header ? "table-row--selected" : ""
            }`}
        >
            {selectable && (
                <Checkbox
                    type="checkbox"
                    isSelected={selected}
                    onClick={setSelected}
                />
            )}

            {values.map((value, index) => {
                const isTransposedHeader = index === 0 && transposed;
                const type = transposed ? types[0] : (types[index] ?? "text");

                if (header || isTransposedHeader) {
                    const sortOrder =
                        columnSort?.index === index
                            ? columnSort.order
                            : "default";

                    return (
                        <TableHeader
                            key={index}
                            value={value}
                            type={type}
                            className={
                                transposed ? "table-header--transposed" : ""
                            }
                            sort={{
                                sortable: !isTransposedHeader,
                                order: sortOrder,
                                setOrder: (order) =>
                                    setColumnSort(index, order),
                            }}
                        />
                    );
                }

                return (
                    <TableData
                        key={index}
                        value={value}
                        type={type}
                        className={transposed ? "table-data--transposed" : ""}
                    />
                );
            })}
        </tr>
    );
}

/**
 * Renders a table header cell with optional sorting support.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {string|number} props.value - Value displayed in the header cell
 * @param {string} [props.type="text"] - Cell renderer type
 * @param {string} [props.className=""] - Additional CSS classes
 * @param {boolean} [props.isSelected=false] - Selection state used by checkbox renderers
 *
 * @param {Object} [props.sort={}] - Sorting configuration
 * @param {boolean} [props.sort.sortable=true] - Whether the column is sortable
 * @param {string} [props.sort.order="default"] - Current sort order
 * @param {(order: string) => void} [props.sort.setOrder] - Updates the sort order
 *
 * @param {() => void} [props.onClick] - Optional click handler used by custom cell renderers
 *
 * @returns {JSX.Element} The rendered table header cell
 */
function TableHeader({
    value,
    type = "text",
    className = "",
    isSelected = false,
    sort = {},
    onClick,
}) {
    const { sortable = true, order = "default", setOrder } = sort;

    const currentSort = sortConfig[order] ?? sortConfig.default;

    const handleSort = () => {
        if (!sortable || !setOrder) {
            return;
        }

        setOrder(currentSort.next);
    };

    const config = cellConfig.header[type] ?? cellConfig.header.text;
    const cellValue = config.render(value, isSelected, onClick);
    const cellBaseClass = config.className;

    const sortIcon = sortable && type !== "checkbox" ? currentSort.icon : null;

    return (
        <th
            className={`table-header ${cellBaseClass} ${className}`}
            onClick={handleSort}
        >
            <div className="table-header__container">
                {cellValue}
                {sortIcon}
            </div>
        </th>
    );
}

/**
 * Renders a table data cell.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {string|number} props.value - Value displayed in the data cell
 * @param {string} [props.type="text"] - Cell renderer type
 * @param {string} [props.className=""] - Additional CSS classes
 * @param {boolean} [props.isSelected=false] - Selection state used by checkbox renderers
 * @param {() => void} [props.onClick] - Optional click handler used by custom cell renderers
 *
 * @returns {JSX.Element} The rendered table data cell
 */
function TableData({
    value,
    type = "text",
    className = "",
    isSelected = false,
    onClick,
}) {
    const config = cellConfig.data[type] ?? cellConfig.data.text;
    const cellValue = config.render(value, isSelected, onClick);
    const cellBaseClass = config.className;

    return (
        <td className={`table-data ${cellBaseClass} ${className}`}>
            {cellValue}
        </td>
    );
}

export default Table;
